import Modal from '../../Unknown/Modal'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import { Box, Button, TextField } from '@mui/material'
import translate from '../../../i18n/translate'
import { useIntl } from 'react-intl'
import { ReactElement } from 'react'
import { Formik, Form } from 'formik'
import { FIELDS, useValidationSchema } from './useValidationSchema'

type ApproveRequestActionModalProps = {
  organizer: any;
  label: ReactElement;
  onSubmit: (
    id: number,
    data?: any,
  ) => Promise<void>;
  buttonText: ReactElement;
}

export default function ApproveRequestActionModal({
  organizer,
  label,
  buttonText,
  onSubmit,
}: ApproveRequestActionModalProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { toggleApproveRequestActionModal } = modalsSlice.actions
  const visible = useAppSelector(state => state.modalsReducer.approveRequestAction)

  const handleSubmit = async (values: any) => {
    try {
       await onSubmit(organizer?.id, {
          earlyWithdrawalFee: Number((values?.[FIELDS.EARLY_WITHDRAWAL_FEE] / 100).toFixed(4)),
          earlyWithdrawalDays: values?.[FIELDS.EARLY_WITHDRAWAL_DAYS],
          earlyWithdrawalPercent: Number((values?.[FIELDS.EARLY_WITHDRAWAL_PERCENT] / 100).toFixed(4)),
        })
      dispatch(toggleApproveRequestActionModal())
    } catch (error) {
    }
  }

  const onCloseModal = () => {
    dispatch(toggleApproveRequestActionModal())
  }

  const validationSchema = useValidationSchema()

  return (
    <Modal
      title={label}
      visible={visible === 'open'}
      style={{ maxWidth: '500px' }}
      setVisible={onCloseModal}
    >
      <Box
        p={2}
        display='flex'
        flexDirection='column'
        justifyContent='center'
        width={1}
        alignItems='center'
        gap={2}
      >
        <Formik
          initialValues={{
            [FIELDS.EARLY_WITHDRAWAL_PERCENT]: organizer?.earlyWithdrawalPercent !== null ? Number((organizer.earlyWithdrawalPercent * 100).toFixed(2)) : null,
            [FIELDS.EARLY_WITHDRAWAL_DAYS]: organizer?.earlyWithdrawalDays ?? null,
            [FIELDS.EARLY_WITHDRAWAL_FEE]: organizer?.earlyWithdrawalFee !== null ? Number((organizer.earlyWithdrawalFee * 100).toFixed(2)) : null,
          }}
          onSubmit={handleSubmit}
          enableReinitialize
          validationSchema={validationSchema}
        >
          {({ errors, values, touched, handleBlur, handleChange, isValid }) => {
            return (
              <Form style={{ width: '100%' }}>
                <Box
                  display='flex'
                  flexDirection='column'
                  justifyContent='center'
                  width={1}
                  alignItems='center'
                  gap={2}
                >
                  <TextField
                    name={FIELDS.EARLY_WITHDRAWAL_PERCENT}
                    value={values?.[FIELDS.EARLY_WITHDRAWAL_PERCENT]}
                    error={touched?.[FIELDS.EARLY_WITHDRAWAL_PERCENT] && Boolean(errors?.[FIELDS.EARLY_WITHDRAWAL_PERCENT])}
                    helperText={touched?.[FIELDS.EARLY_WITHDRAWAL_PERCENT] && String(errors?.[FIELDS.EARLY_WITHDRAWAL_PERCENT])}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    fullWidth
                    type='number'
                    label={intl.formatMessage({
                      id: 'organizerEarlyWithdrawalPercent'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'organizerEarlyWithdrawalPercent'
                    })}
                    dir='ltr'
                    sx={{
                      '.MuiInputBase-root.MuiInput-root:before': {
                        borderBottom: '0px'
                      },
                      '.MuiInputBase-root.MuiInput-root:hover': {
                        borderBottom: '0px'
                      }
                    }}
                  />
                  <TextField
                    name={FIELDS.EARLY_WITHDRAWAL_DAYS}
                    value={values?.[FIELDS.EARLY_WITHDRAWAL_DAYS]}
                    error={touched?.[FIELDS.EARLY_WITHDRAWAL_DAYS] && Boolean(errors?.[FIELDS.EARLY_WITHDRAWAL_DAYS])}
                    helperText={touched?.[FIELDS.EARLY_WITHDRAWAL_DAYS] && String(errors?.[FIELDS.EARLY_WITHDRAWAL_DAYS])}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    fullWidth
                    type='number'
                    label={intl.formatMessage({
                      id: 'organizerEarlyWithdrawalDays'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'organizerEarlyWithdrawalDays'
                    })}
                    dir='ltr'
                    sx={{
                      '.MuiInputBase-root.MuiInput-root:before': {
                        borderBottom: '0px'
                      },
                      '.MuiInputBase-root.MuiInput-root:hover': {
                        borderBottom: '0px'
                      }
                    }}
                  />
                  <TextField
                    name={FIELDS.EARLY_WITHDRAWAL_FEE}
                    value={values?.[FIELDS.EARLY_WITHDRAWAL_FEE]}
                    error={touched?.[FIELDS.EARLY_WITHDRAWAL_FEE] && Boolean(errors?.[FIELDS.EARLY_WITHDRAWAL_FEE])}
                    helperText={touched?.[FIELDS.EARLY_WITHDRAWAL_FEE] && String(errors?.[FIELDS.EARLY_WITHDRAWAL_FEE])}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    fullWidth
                    type='number'
                    label={intl.formatMessage({
                      id: 'organizerEarlyWithdrawalFee'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'organizerEarlyWithdrawalFee'
                    })}
                    dir='ltr'
                    sx={{
                      '.MuiInputBase-root.MuiInput-root:before': {
                        borderBottom: '0px'
                      },
                      '.MuiInputBase-root.MuiInput-root:hover': {
                        borderBottom: '0px'
                      }
                    }}
                  />
                </Box>
                <Box
                  mt={2}
                  display='flex'
                  flexDirection='column'
                  justifyContent='center'
                  width={1}
                  alignItems='center'
                  gap={1}
                >
                  <Button
                    size='large'
                    variant='outlined'
                    color='warning'
                    fullWidth
                    onClick={onCloseModal}
                  >
                    {translate('cancel')}
                  </Button>
                  <Button
                    size='large'
                    variant='contained'
                    color='warning'
                    fullWidth
                    disabled={!isValid}
                    type='submit'
                  >
                    {buttonText}
                  </Button>
                </Box>
              </Form>
            )
          }}
        </Formik>
      </Box>
    </Modal>
  )
}
